import React from 'react';
import { useMutation } from '@apollo/client';
import { Button, Spinner } from 'react-bootstrap';
import { FaRedo, FaUndo } from 'react-icons/fa';
import {
  FieldDatepicker,
  FieldInput,
  FieldTextArea,
  FormCol,
  FormCol12,
  FormRow,
  promiseModalDialog,
  toastSuccess,
} from 'motion-components';
import { useTranslation } from 'react-i18next';
import { useHandleError } from 'shared-pages/services';
import { useTriggerMailService } from 'shared-pages/hooks/trigger-mail-service';
import { gqlRestoreMailMutation, gqlRetryFailedMailMutation } from '../graphql';

export const FormFields = ({ data, onRestored }: { data?: any; onRestored: () => void }) => {
  const { t } = useTranslation();
  const { handleGraphqlError } = useHandleError('RestoreMail');
  const { triggerMailService } = useTriggerMailService();
  const [restoreMail, { loading: restoring }] = useMutation(gqlRestoreMailMutation);
  const [retryFailedMail, { loading: resending }] = useMutation(gqlRetryFailedMailMutation);
  const canEdit = false;
  const html = data?.html;
  const isDeleted = Boolean(data?.isDeleted);
  // Το trigger στέλνει μόνο όσα απέτυχαν και δεν έχουν ήδη σταλεί / ακυρωθεί
  const hasFailed = Boolean(data?.failedDate || data?.failedReason || data?.failedDetails);
  const canResend =
    Boolean(data?.id) &&
    !isDeleted &&
    // !data?.sendDate &&
    // Boolean(data?.failedDate || data?.failedReason || data?.failedDetails)
    true;

  /** Επαναφέρει το ακυρωμένο mail στην ουρά, ώστε να είναι ξανά διαθέσιμο για αποστολή */
  const handleRestore = async () => {
    const confirmed = await promiseModalDialog({
      title: t('Επαναφορά αποστολής'),
      text: t('Το μήνυμα θα επανέλθει στην ουρά αποστολής. Θέλετε να συνεχίσετε;'),
    });
    if (!confirmed) return;

    try {
      const result = await restoreMail({ variables: { id: data.id } });
      if (!result.data?.updateMailsrepo?.id) {
        throw new Error(t('Η επαναφορά του μηνύματος απέτυχε'));
      }
      toastSuccess(t('Το μήνυμα επανήλθε στην ουρά αποστολής'));
      onRestored();
    } catch (error) {
      handleGraphqlError('mutation-restore', error);
    }
  };

  /** Καθαρίζει το σφάλμα και ενεργοποιεί ξανά την ουρά αποστολής */
  const handleResend = async () => {
    const confirmed = await promiseModalDialog({
      title: t('Επαναποστολή'),
      text: t('Το μήνυμα θα ξαναμπεί στην ουρά αποστολής. Θέλετε να συνεχίσετε;'),
    });
    if (!confirmed) return;

    try {
      const result = await retryFailedMail({ variables: { id: data.id } });
      if (!result.data?.retryFailedMail?.id) {
        throw new Error(t('Η επαναποστολή του μηνύματος απέτυχε'));
      }
      triggerMailService();
      toastSuccess(t('Η επαναποστολή του μηνύματος ενεργοποιήθηκε'));
      onRestored();
    } catch (error) {
      handleGraphqlError('mutation-resend', error);
    }
  };

  const renderResendButton = () => (
    <Button
      type="button"
      size="sm"
      variant="outline-success"
      className="button-animate-scale"
      disabled={resending}
      onClick={handleResend}
    >
      {resending ? (
        <Spinner animation="border" size="sm" />
      ) : (
        <span className="d-flex align-items-center">
          <FaRedo className="me-2 button-icon" />
          {t('Επαναποστολή')}
        </span>
      )}
    </Button>
  );

  return (
    <>
      {isDeleted && (
        <div className="d-flex align-items-center mb-3">
          <div className="text-danger fw-bold">{t('Ακυρώθηκε η Αποστολή')}</div>
          <Button
            type="button"
            size="sm"
            variant="outline-success"
            className="button-animate-scale ms-3"
            disabled={restoring}
            onClick={handleRestore}
          >
            {restoring ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <span className="d-flex align-items-center">
                <FaUndo className="me-2 button-icon" />
                {t('Επαναφορά στην ουρά αποστολής')}
              </span>
            )}
          </Button>
        </div>
      )}
      {(canResend || hasFailed) && (
        <div className="d-flex align-items-center mb-3">
          {hasFailed && <div className="text-danger fw-bold">{t('Αποτυχία Αποστολής')}</div>}
          {canResend && <div className="ms-3">{renderResendButton()}</div>}
        </div>
      )}
      <FormRow>
        <FieldInput disabled={!canEdit} as={FormCol} label={t('Αποστολέας')} name="from" />
        <FieldInput disabled={!canEdit} as={FormCol} label={t('Παραλήπτης')} name="to" />
        <FieldInput disabled={!canEdit} as={FormCol} label={t('CC')} name="cc" />
      </FormRow>
      <FormRow>
        <FieldInput disabled={!canEdit} as={FormCol} label={t('Προέλευση')} name="originatedFrom" />
        <FieldDatepicker
          disabled={!canEdit}
          as={FormCol}
          label={t('Ημερομηνία Σύνταξης')}
          name="composedDate"
          showTimeSelect
          dateFormat="d/M/yyyy HH:mm"
          timeFormat="HH:mm"
          timeIntervals={15}
        />
        <FieldDatepicker
          disabled={!canEdit}
          as={FormCol}
          label={t('Ημερομηνία Αποστολής')}
          name="sendDate"
          showTimeSelect
          dateFormat="d/M/yyyy HH:mm"
          timeFormat="HH:mm"
          timeIntervals={15}
        />
      </FormRow>
      <FormRow>
        <FieldInput disabled={!canEdit} as={FormCol12} label={t('Θέμα')} name="subject" />
      </FormRow>
      <FormRow>
        <FormCol12>
          <div className="mb-2">{t('Περιεχόμενο')}</div>
          {html ? (
            <div
              className="bg-white border rounded p-3"
              style={{ maxHeight: '420px', overflow: 'auto' }}
            >
              {/* Το σώμα του mail αποθηκεύεται ως HTML στην ουρά αποστολής */}
              <div dangerouslySetInnerHTML={{ __html: html }} />
            </div>
          ) : (
            <FieldTextArea disabled={!canEdit} label="" name="text" rows={8} />
          )}
        </FormCol12>
      </FormRow>
      <FormRow>
        <FieldDatepicker
          disabled={!canEdit}
          as={FormCol}
          label={t('Ημερομηνία Αποτυχίας')}
          name="failedDate"
          showTimeSelect
          dateFormat="d/M/yyyy HH:mm"
          timeFormat="HH:mm"
          timeIntervals={15}
        />
        <FieldInput
          disabled={!canEdit}
          as={FormCol}
          label={t('Λόγος Αποτυχίας')}
          name="failedReason"
        />
        {canResend && (
          <FormCol className="d-flex align-items-end pb-3">{renderResendButton()}</FormCol>
        )}
      </FormRow>
      <FormRow>
        <FieldTextArea
          disabled={!canEdit}
          as={FormCol12}
          label={t('Λεπτομέρειες Αποτυχίας')}
          name="failedDetails"
          rows={4}
        />
      </FormRow>
    </>
  );
};
